import React, { useState, useEffect, useRef } from 'react'
import { Button, Form } from 'antd'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { store as reduxStore } from 'index'
import { setPagination, useIsMobile } from 'utils'
import sanitizeHtml from 'utils/sanitize'
import { getTempEnvParam } from 'env'
import { useSelector } from 'react-redux'
import { SERVICE_NAME } from 'service'
import { getTotpSecretLength, SHORT_TOTP_SECRET_LEN } from 'services/totp'
import { totpFingerprint, hasFingerprint } from 'services/totp/duplicateRegistry'
import {
  confirmDelete,
  confirmDiscard,
  errorTranslationMessage,
  ArrowLeftIcon,
  confirmBase,
} from 'utils/ui'
import {
  getProcessEmailList,
  EMAIL_REGEX,
  sendDarkWebService,
  checkLeakedRecord,
  getZdwExpiredAt,
} from 'dUtils'
import ResourceService from 'api/Resource'
import { FieldManager, SRC_TYPE } from 'pages/records/components/helper'
import { featureFlags } from 'config/feature-flags'
import { uploadFilesToS3, mapUploadResultsToSendData } from 'utils/importExport/utils/s3Upload'

import styles from 'pages/records/components/CardView/style.module.scss'
import closeIcon from '@/assets/images/icon-close-24.svg'

import CardView from './CardView'

const disableCreateBtn = (value) => {
  reduxStore.dispatch({
    type: 'menu/SET_STATE',
    payload: {
      disableCreate: value,
    },
  })
}

export function urlValidator(value, t) {
  if (value == null || value.length === 0) {
    return Promise.resolve()
  }
  if (value != null && value.length > 0) {
    const urlRegex =
      /^(https?:\/\/)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/g
    const isURL = urlRegex.test(value)

    if (isURL === true) {
      return Promise.resolve()
    }
  }
  return Promise.reject(new Error(t('validation.url.invalid')))
}

export const handleCancelSubmit = ({ openItem, id, disableSave, t, content }) => {
  const goToDetail = () => {
    if (id != null) {
      openItem('detail', id)
    } else {
      openItem(null)
    }
  }

  if (!disableSave) {
    confirmDiscard({
      title: t('record.title.editDiscard'),
      content,
      submit: goToDetail,
    })
  } else if (id != null) {
    openItem('detail', id)
  } else {
    openItem(null)
  }
}

const EditContent = ({
  openItem,
  id,
  messageApi,
  individual,
  reload,
  setProcessing,
  getRsKeyObjFromResource,
  type,
  pageInfo,
}) => {
  const [attachmentLoading, setAttachmentLoading] = useState(false)
  const [disableSave, setDisableSave] = useState(true)

  const [fieldManager, setFieldManager] = useState(null)
  const [formInfo, setFormInfo] = useState(null)

  const { t } = useTranslation()
  const ref = useRef()

  const { isLeaked: isLeakedInSettings } = reduxStore.getState().settings

  const antMessage = (text, msgType = 'success') => {
    messageApi.open({
      type: msgType,
      content: text,
    })
  }

  const editSuccessMessage = () =>
    antMessage(t('workspace.record.edit', { service: t(SERVICE_NAME) }))
  const createSuccessMessage = () =>
    antMessage(t('workspace.record.create', { service: t(SERVICE_NAME) }))
  const deleteSuccessMessage = () =>
    antMessage(t('workspace.record.delete', { service: t(SERVICE_NAME) }))

  const closeItem = () => {
    openItem('detail', id)
  }

  const initializeFieldManager = async () => {
    const RsKeyObj = await getRsKeyObjFromResource(individual)

    const attachmentOptions = {
      loadingChanged: (isAttachmentLoading) => {
        setAttachmentLoading(isAttachmentLoading)
      },
      lengthChanged: () => {},
    }
    const onStateChanged = ({ formInfo: newFormInfo, changed }) => {
      console.log(' --- changed : ', changed)
      if (newFormInfo != null) {
        setFormInfo(newFormInfo)
        if (ref.current != null && newFormInfo.initialValues != null) {
          ref.current.setFieldsValue(newFormInfo.initialValues)
        }
      }

      if (type === 'create') {
        setDisableSave(false)
        return
      }

      if (changed != null) {
        setDisableSave(changed !== true)
      }
    }

    const fieldMgr = new FieldManager(
      individual,
      RsKeyObj,
      ref,
      onStateChanged,
      attachmentOptions,
      messageApi,
      individual?.leakedData,
    )
    console.log(fieldMgr)
    setFieldManager(fieldMgr)
  }

  useEffect(() => {
    initializeFieldManager()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [individual])

  const { zeroDarkWebSettings: zeroDarkWebSettingsState } = reduxStore.getState().settings
  const isZeroDarkWebActive = zeroDarkWebSettingsState?.zdw_active === 'true'

  // SUBMIT
  const handleSubmit = async (values, allFields = [], sendNotification = false) => {
    setDisableSave(true)
    console.log(' --- allFields : ', allFields)

    const updateKeyProps = {
      expired_at: id == null ? new Date().toISOString : values.updated_at,
    }

    const { files, sendData } = await fieldManager.getSendData(updateKeyProps)
    console.log(' ---- send data : ', sendData, JSON.stringify(sendData))

    try {
      let response = null

      // S3 업로드 진행하는 방식
      if (featureFlags.usePresignedUpload) {
        // 추가된 파일이 있을 때만 presigned URL 받아서 S3 업로드 진행
        if (files.length > 0) {
          const fileMeta = files.map((file) => ({
            filename: file.name,
            content_type: file.type || 'application/octet-stream',
            size: file.size,
          }))

          // 먼저 presigned URL을 받아옴
          const urlResponse = await ResourceService.getUploadUrls(
            fileMeta,
            id != null ? individual.id : undefined,
          )

          // 그 다음 S3에 파일 업로드
          const { resource_id: presignedResourceId, files: uploadFiles } = urlResponse.data
          const uploadResults = await uploadFilesToS3(files, uploadFiles)
          mapUploadResultsToSendData(sendData, uploadResults)
          sendData.resource_id = presignedResourceId
        }

        if (id != null) {
          response = await ResourceService.putPresigned({
            id: individual.id,
            data: sendData,
            sendNotification,
          })
        } else {
          response = await ResourceService.createPresigned(sendData)
        }
        // 기존 방식 - 파일이 있을 때는 multipart/form-data로 전송, 없을 때는 application/json으로 전송
      } else {
        const formData = new FormData()
        formData.append('data', JSON.stringify(sendData))
        files.forEach((file) => {
          formData.append('files', file)
        })

        if (id != null) {
          response = await ResourceService.put({
            id: individual.id,
            request: formData,
            sendNotification,
          })
        } else {
          response = await ResourceService.create(formData)
        }
      }

      if (response.code === 200) {
        const resourceId = id != null ? id : response.data.resource_id

        if (isZeroDarkWebActive) {
          if (allFields !== false) {
            const request = {
              resources: [
                {
                  resource_id: resourceId,
                  is_leaked: true,
                  leaked_content: {
                    ...allFields,
                    source: 'ZERODARKWEB',
                  },
                },
              ],
            }

            const bulkPutLeakedResponse = await ResourceService.bulkPutLeaked(request)
            if (bulkPutLeakedResponse.code !== 200) {
              console.error('❌ Error putting leaked data:', bulkPutLeakedResponse.message)
              antMessage(t('record.error.failedZeroDarkWeb'), 'error')
              setProcessing(false)
              setDisableSave(false)
              disableCreateBtn(false)
              return
            }
          }
        }

        if (allFields === false && isLeakedInSettings === true) {
          openItem(null)
        } else {
          const pageOption = id == null ? { removePage: true } : {}
          openItem('detail', resourceId, null, pageOption)
          reduxStore.dispatch({
            type: 'menu/RELOAD',
          })
        }

        if (isZeroDarkWebActive) {
          reduxStore.dispatch({
            type: 'settings/SET_SETTINGS',
            payload: {
              zeroDarkWebSettings: {
                zdw_checked_at: new Date().toISOString(),
                zdw_expired_at: getZdwExpiredAt(),
              },
            },
          })
        }

        const tablePage = getTempEnvParam('curTablePage')
        if (id != null) {
          editSuccessMessage()
        } else {
          createSuccessMessage()
        }
        if (tablePage) {
          tablePage.reload()
        }

        setProcessing(false)
      }
    } catch (error) {
      console.error(error)
      if (error != null) {
        errorTranslationMessage(error.code, t, messageApi)
      }
    } finally {
      setProcessing(false)
      setDisableSave(false)
      disableCreateBtn(false)
    }
  }
  const { isSendNotification } = useSelector((state) => state.settings)

  // TOTP secret은 체크섬이 없어 형식 이상의 검증이 불가능 — 변경 저장 시 사용자가 사이트 코드와
  // 대조했는지 확인한다(잘못된 키는 동작하던 2단계 인증을 조용히 망가뜨림). PR5.5(#1493).
  // confirmDiscardChanges(utils/ui)와 동일한 Promise<boolean> 패턴.
  const confirmTotpChange = (hasShortKey, duplicateTitle) =>
    new Promise((resolve) => {
      confirmBase({
        icon: 'blue',
        title: t('record.totp.confirmChangeTitle'),
        content: (
          <span>
            {t('record.totp.confirmChangeContent')}
            {hasShortKey ? (
              <React.Fragment>
                <br />
                <br />
                {'⚠ '}
                {t('record.totp.confirmChangeShortKey')}
              </React.Fragment>
            ) : null}
            {duplicateTitle != null ? (
              <React.Fragment>
                <br />
                <br />
                {'⚠ '}
                {t('record.totp.confirmChangeDuplicate', { title: duplicateTitle })}
              </React.Fragment>
            ) : null}
          </span>
        ),
        width: 480,
        okText: t('common.button.yes'),
        cancelText: t('common.button.no'),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      })
    })

  const handleCustomSubmit = async () => {
    const successCheck = fieldManager.preCheckForm()

    try {
      const res = await ref.current.validateFields()

      let zeroDarkWebResponse = null
      let processNewLeakedData = []

      const allFields = Object.values(fieldManager.getCurrentFormValues())

      setProcessing(true)
      disableCreateBtn(true)

      if (isZeroDarkWebActive) {
        const requestToZeroDarkWeb = []
        allFields.forEach((item) => {
          if (item) {
            const matches = String(item).match(EMAIL_REGEX)
            if (matches) {
              requestToZeroDarkWeb.push(...matches)
            }
          }
        })

        const uniqueEmails = Array.from(new Set(requestToZeroDarkWeb))
        if (uniqueEmails.length > 0) {
          try {
            zeroDarkWebResponse = await sendDarkWebService(uniqueEmails)
            if (zeroDarkWebResponse.code !== 200) {
              console.error('❌ Error sending DarkWebService:', zeroDarkWebResponse.message)
              return
            }
            processNewLeakedData = getProcessEmailList(zeroDarkWebResponse.results)
          } catch (error) {
            console.error('❌ Error sending DarkWebService:', error)
          } finally {
            setProcessing(false)
            disableCreateBtn(false)
          }
        }
      }

      const isSentMail =
        individual?.id != null &&
        (individual?.share?.length > 0 || individual?.permission === SRC_TYPE.GROUP_ADMIN)

      // recordFields에서 이메일 형식이면 이메일 객체 안에 아니면 contents 배열 안에 넣는 함수
      const classifyRecordFields = (fields) => {
        const emails = []
        const contents = []
        fields.forEach((field) => {
          if (typeof field === 'string' && EMAIL_REGEX.test(field.trim())) {
            emails.push(field.trim())
          } else if (
            field &&
            typeof field.content === 'string' &&
            EMAIL_REGEX.test(field.content.trim())
          ) {
            emails.push(field.content.trim())
          } else if (field && typeof field.content === 'string') {
            contents.push(field.content.trim())
          } else if (typeof field === 'string') {
            contents.push(field.trim())
          }
        })
        return { email: emails, content: contents }
      }

      const recordFields = allFields.slice(1)
      const recordFieldsClassified = classifyRecordFields(recordFields, processNewLeakedData)
      const isLeaked = checkLeakedRecord(recordFieldsClassified, processNewLeakedData, true)
      const submit = (send) => handleSubmit(res, isLeaked, send)

      const confirmSendNotification = () => {
        if (isSentMail && successCheck && isSendNotification) {
          confirmBase({
            icon: 'blue',
            title: t('record.title.notifyOthers'),
            content: (
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    t('record.desc.notifyOthers', { service: t(SERVICE_NAME) }).replace(
                      /\n/g,
                      '<br />',
                    ),
                  ),
                }}
              />
            ),
            width: 480,
            okText: t('common.button.yes'),
            cancelText: t('common.button.no'),
            onOk: () => submit(true),
            onCancel: () => submit(false),
          })
        } else {
          submit(false)
        }
      }

      if (successCheck) {
        // ?.와 ?? []: RecordEdit.test의 FieldManager mock(brownfield lint 부채 파일이라 무수정)에
        // 메서드가 없어도 안전 — 프로덕션 FieldManager는 항상 메서드를 가진다(설계 §기존테스트영향).
        const changedTotpIds = fieldManager.getChangedTotpFieldIds?.(res) ?? []
        if (changedTotpIds.length > 0) {
          const hasShortKey = changedTotpIds.some((fid) => {
            const len = getTotpSecretLength(res[fid])
            return len != null && len < SHORT_TOTP_SECRET_LEN
          })
          // 중복 경고(#1506) — 세션 캐시(허브 방문분)와만 비교(전수 복호 없음 — 지연 0).
          // 캐시 미방문 상태에서는 미동작(확정 한계 — 허브 배지가 사후 보완).
          // 자기 자신(레코드 편집으로 secret 유지) 지문과의 오탐은 individual.id 제외로 방지.
          const duplicateOf = changedTotpIds
            .map((fid) => hasFingerprint(totpFingerprint(res[fid]), individual?.id))
            .find(Boolean)
          const proceed = await confirmTotpChange(hasShortKey, duplicateOf?.title)
          if (!proceed) {
            // 기존 leaked confirm의 onCancel은 복구를 안 하지만(기존 wart — 보고만),
            // 이 취소 경로는 저장 버튼 잠금을 명시 복구한다
            setProcessing(false)
            disableCreateBtn(false)
            return
          }
        }

        if (isLeaked != null && isLeaked !== false) {
          confirmBase({
            icon: 'blue',
            title: t('dark-web.confirm.title'),
            content: t('dark-web.confirm.description', { service: t(SERVICE_NAME) }),
            width: 480,
            okText: t('common.button.yes'),
            cancelText: t('common.button.no'),
            onOk: confirmSendNotification,
            onCancel: () => {},
          })
          return
        }

        if (individual?.id != null && individual?.is_leaked === true) {
          const request = {
            resources: [{ resource_id: individual.id, is_leaked: false }],
          }

          const response = await ResourceService.bulkPutLeaked(request)

          if (response.code !== 200) {
            console.error('❌ Error putting leaked data:', response.message)
            antMessage(t('record.error.failedZeroDarkWeb'), 'error')
            return
          }
        }

        confirmSendNotification()
      }
    } catch (error) {
      console.log(' --- error : ', error)
    }
  }

  const handleCancel = () => {
    const goToDetail = () => {
      if (id != null) {
        openItem('detail', id)
      } else {
        openItem(null)
      }
    }
    if (fieldManager.checkChanged()) {
      confirmDiscard({
        title: t('record.title.createDiscard', { service: t(SERVICE_NAME) }),
        content: t('record.desc.createDiscard', { service: t(SERVICE_NAME) }),
        submit: goToDetail,
      })
    } else {
      openItem(null)
    }
  }

  const handleDeleteSubmit = async () => {
    let ret
    setDisableSave(true)
    try {
      ret = await ResourceService.delete({ id: individual.id })
      // 삭제 후엔 상세 패널을 닫고 targetId 를 먼저 정리한다. reload 보다 반드시 먼저 호출해야
      // reload 가 이미 삭제된 레코드의 targetId 를 findTargetRecordPage 로 쫓으며 setPagination 의
      // 페이지 이동과 충돌하는 것을 막는다 (search-table-page-target-id 룰 / PR #1327 패턴).
      // 이 충돌이 "페이지네이션은 9페이지인데 목록은 빈 10페이지" 불일치를 만들었다.
      openItem(null)
      if (ret.code === 200) {
        setPagination(pageInfo)
        reload(deleteSuccessMessage)
        reduxStore.dispatch({
          type: 'menu/RELOAD',
        })
      }
      setDisableSave(false)
    } catch (error) {
      if (error != null) {
        errorTranslationMessage(error.code, t, messageApi)
      }
      closeItem()
    }
  }

  const isMobile = useIsMobile()

  const header = (
    <React.Fragment>
      {isMobile ? (
        <div className={styles.mobileHeader}>
          <ArrowLeftIcon onClick={() => openItem(null)} />
          <b>{id === null ? t('chat.button.create') : t('common.button.edit')}</b>
        </div>
      ) : (
        <React.Fragment>
          <b>{id === null ? t('chat.button.create') : t('common.button.edit')}</b>
          <div>
            <Button
              type="text"
              icon={<img src={closeIcon} alt="delete" />}
              onClick={() => (id != null ? handleCancel() : openItem(null))}
            />
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  )

  const disableSaveBtn = disableSave || attachmentLoading
  const { formItems, formAttrs: formAttrsEx, initialValues } = formInfo || {}
  const formAttrs = {
    className: classNames(styles.formStyle),
    name: 'form',
    layout: 'vertical',
    initialValues,
    validateTrigger: [],
    ref,
    ...formAttrsEx,
  }

  const formElem = (
    <Form {...formAttrs}>
      <div className={classNames(styles.CheckboxContents)}>{formItems}</div>

      <div className={classNames(styles.ButtonContainer)}>
        {id == null ? (
          <React.Fragment>
            <div>{null}</div>
            <div>
              <Button
                type="button"
                className={classNames(styles.CancelButton)}
                onClick={handleCancel}
              >
                {t('common.button.cancel')}
              </Button>
              <Button
                type="primary"
                disabled={disableSaveBtn}
                className={classNames(styles.SaveButton, 'type1')}
                onClick={handleCustomSubmit}
              >
                {t('common.button.save')}
              </Button>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            {individual != null && individual.permission === SRC_TYPE.RESOURCE_OWNER ? (
              <Button
                type="button"
                className={classNames(styles.DeleteButton, 'delete')}
                onClick={() =>
                  confirmDelete({
                    icon: 'red',
                    title: t('appConfig.title.deleteRecord', { service: t(SERVICE_NAME) }),
                    content: t('appConfig.confirm.deleteRecord', {
                      record: individual.title,
                      service: t(SERVICE_NAME),
                    }),
                    okText: t('common.button.delete'),
                    onOk: handleDeleteSubmit,
                  })
                }
              >
                {t('common.button.delete')}
              </Button>
            ) : (
              <div>{null}</div>
            )}

            <div>
              <Button
                type="submit"
                className={classNames(styles.CancelButton)}
                onClick={() =>
                  handleCancelSubmit({
                    openItem,
                    id,
                    disableSave,
                    t,
                    content: t('record.desc.editDiscard', { service: t(SERVICE_NAME) }),
                  })
                }
              >
                {t('common.button.cancel')}
              </Button>
              <Button
                type="primary"
                disabled={disableSaveBtn}
                className={classNames(styles.SaveButton, 'type1')}
                onClick={handleCustomSubmit}
              >
                {t('common.button.save')}
              </Button>
            </div>
          </React.Fragment>
        )}
      </div>
    </Form>
  )

  return (
    <CardView header={header}>
      <div className={classNames(styles.cardContainer)}>{formElem}</div>
    </CardView>
  )
}

export default EditContent
